<template>
  <div>
    <div class="data-table-shell">
      <div class="data-table-scroll">
        <div v-if="loading" class="w-full">
          <table class="data-table">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">{{ text.proposalInfo }}</th>
                <th v-if="showCustomerColumn" class="data-table-th text-left">{{ text.customer }}</th>
                <th v-if="showCompanyColumn" class="data-table-th text-left">{{ text.company }}</th>
                <th class="data-table-th text-left">{{ text.owner }}</th>
                <th class="data-table-th is-right">{{ text.totalAmount }}</th>
                <th class="data-table-th is-right">{{ text.payableAmount }}</th>
                <th class="data-table-th is-center">{{ text.status }}</th>
                <th class="data-table-th is-center">{{ text.createTime }}</th>
                <th class="data-table-th is-center">{{ text.action }}</th>
              </tr>
            </thead>
            <tbody class="data-table-body data-table-skeleton">
              <tr v-for="n in 5" :key="n" class="animate-pulse">
                <td class="data-table-td">
                  <div class="flex items-center gap-3">
                    <div class="h-10 w-10 rounded-lg bg-slate-200"></div>
                    <div>
                      <div class="mb-2 h-4 w-32 rounded bg-slate-200"></div>
                      <div class="h-3 w-24 rounded bg-slate-200"></div>
                    </div>
                  </div>
                </td>
                <td v-if="showCustomerColumn" class="data-table-td"><div class="h-4 w-20 rounded bg-slate-200"></div></td>
                <td v-if="showCompanyColumn" class="data-table-td"><div class="h-4 w-24 rounded bg-slate-200"></div></td>
                <td class="data-table-td"><div class="h-4 w-20 rounded bg-slate-200"></div></td>
                <td class="data-table-td is-right"><div class="ml-auto h-4 w-20 rounded bg-slate-200"></div></td>
                <td class="data-table-td is-right"><div class="ml-auto h-4 w-20 rounded bg-slate-200"></div></td>
                <td class="data-table-td is-center"><div class="mx-auto h-6 w-16 rounded-full bg-slate-200"></div></td>
                <td class="data-table-td is-center"><div class="mx-auto h-4 w-24 rounded bg-slate-200"></div></td>
                <td class="data-table-td"><div class="flex justify-center gap-2"><div class="h-8 w-8 rounded bg-slate-200"></div></div></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="plans.length === 0" :class="[emptyStateClass, compactEmpty ? 'data-table-empty is-compact' : 'data-table-empty']">
          <template v-if="compactEmpty">
            {{ text.empty }}
          </template>
          <template v-else>
            <div class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
              <svg class="h-10 w-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <h3 class="mb-2 text-lg font-medium text-slate-900">{{ text.empty }}</h3>
            <p class="text-slate-500">{{ emptyDescription }}</p>
          </template>
        </div>

        <table v-else class="data-table">
          <thead class="data-table-head">
            <tr>
              <th class="data-table-th text-left">{{ text.proposalInfo }}</th>
              <th v-if="showCustomerColumn" class="data-table-th text-left">{{ text.customer }}</th>
              <th v-if="showCompanyColumn" class="data-table-th text-left">{{ text.company }}</th>
              <th class="data-table-th text-left">{{ text.owner }}</th>
              <th class="data-table-th is-right">{{ text.totalAmount }}</th>
              <th class="data-table-th is-right">{{ text.payableAmount }}</th>
              <th class="data-table-th is-center">{{ text.status }}</th>
              <th class="data-table-th is-center">{{ text.createTime }}</th>
              <th class="data-table-th is-center">{{ text.action }}</th>
            </tr>
          </thead>
          <tbody class="data-table-body">
            <tr
              v-for="plan in plans"
              :key="plan.id"
              class="data-table-row cursor-pointer"
              @click="emit('detail', plan)"
            >
              <td class="data-table-td">
                <div class="flex items-center gap-3">
                  <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                    <svg class="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="data-table-cell-title">{{ plan.name }}</div>
                    <div class="data-table-cell-meta">{{ plan.planNo }}</div>
                  </div>
                </div>
              </td>

              <td v-if="showCustomerColumn" class="data-table-td">
                <button
                  v-if="customerLinkable && plan.customerId"
                  class="text-sm text-primary-600 hover:text-primary-800 hover:underline"
                  @click.stop="emit('customer-click', plan)"
                >
                  {{ plan.customerName || '-' }}
                </button>
                <span v-else class="text-sm text-slate-600">{{ plan.customerName || '-' }}</span>
              </td>

              <td v-if="showCompanyColumn" class="data-table-td text-sm text-slate-600">{{ plan.companyName || '-' }}</td>

              <td class="data-table-td text-sm text-slate-600">
                <div class="flex items-center gap-2">
                  <div class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-50">
                    <svg class="h-3 w-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <span>{{ plan.ownerUserName || '-' }}</span>
                </div>
              </td>

              <td class="data-table-td is-right text-sm font-medium text-slate-900">¥{{ formatAmount(plan.totalAmount) }}</td>
              <td class="data-table-td is-right text-sm font-medium text-primary-600">¥{{ formatAmount(plan.payableAmount) }}</td>

              <td class="data-table-td is-center" @click.stop>
                <span v-if="plan.isDraft === 1" class="status-badge mr-1 bg-slate-100 text-slate-600">
                  {{ text.draft }}
                </span>
                <span v-else :class="['status-badge', getStatusClass(plan.status)]">
                  {{ getStatusText(plan.status) }}
                </span>
                <span v-if="plan.needApproval" class="ml-1 inline-flex text-amber-500" :title="text.needApproval">
                  <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                </span>
                <button
                  v-if="showApprovalAction(plan)"
                  class="table-action-button is-purple ml-1 cursor-pointer"
                  :title="text.viewApproval" :aria-label="text.viewApproval"
                  @click.stop="emit('approval-detail', plan)"
                >
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                  </svg>
                </button>
              </td>

              <td class="data-table-td is-center text-sm text-slate-600">{{ $formatDateTime(plan.createTime) }}</td>

              <td class="data-table-td" @click.stop>
                <div class="flex items-center justify-center gap-1">
                  <button
                    class="table-action-button is-primary"
                    :title="text.viewDetail" :aria-label="text.viewDetail"
                    @click="emit('detail', plan)"
                  >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </button>
                  <button
                    v-permission="'plan:edit'"
                    v-if="canEdit(plan)"
                    class="table-action-button is-primary"
                    :title="text.edit" :aria-label="text.edit"
                    @click="emit('edit', plan)"
                  >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <AppPagination
      v-if="plans.length > 0 && showPagination"
      :pagination="pagination"
      :page-sizes="[10, 20, 50]"
      :class="paginationClass"
      :total-label="`${text.total} `"
      :total-suffix="` ${text.item}`"
      :page-size-label="text.perPage"
      :page-size-suffix="text.item"
      :prev-text="text.prev"
      :next-text="text.next"
      @page-change="emit('page-change', $event)"
      @size-change="emit('size-change', $event)"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import AppPagination from '@/components/AppPagination.vue'

const text = {
  proposalInfo: '方案信息',
  customer: '客户',
  company: '客户企业',
  owner: '销售人员',
  totalAmount: '总金额',
  payableAmount: '应付金额',
  status: '状态',
  createTime: '创建时间',
  action: '操作',
  empty: '暂无方案数据',
  draft: '草稿',
  executing: '执行中',
  completed: '已完成',
  cancelled: '已取消',
  pendingApproval: '待审批',
  approvalRejected: '审批驳回',
  unknown: '未知',
  needApproval: '当前方案需要审批',
  viewApproval: '查看审批详情',
  viewDetail: '查看详情',
  edit: '编辑',
  total: '共',
  item: '条',
  prev: '上一页',
  next: '下一页',
  perPage: '每页'
}

const props = defineProps({
  plans: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  pagination: {
    type: Object,
    default: () => ({
      current: 1,
      page: 1,
      size: 20,
      total: 0,
      pages: 0
    })
  },
  showCustomerColumn: { type: Boolean, default: true },
  showCompanyColumn: { type: Boolean, default: true },
  customerLinkable: { type: Boolean, default: true },
  showPagination: { type: Boolean, default: true },
  compactEmpty: { type: Boolean, default: false },
  emptyDescription: {
    type: String,
    default: '\u60a8\u53ef\u4ee5\u521b\u5efa\u4e00\u4e2a\u65b0\u7684\u62a5\u4ef7\u65b9\u6848\u6765\u5f00\u59cb\u3002'
  }
})

const emit = defineEmits(['detail', 'edit', 'approval-detail', 'customer-click', 'page-change', 'size-change'])

const statusMap = {
  0: { text: text.draft, class: 'bg-slate-100 text-slate-600' },
  1: { text: text.executing, class: 'bg-blue-100 text-blue-700' },
  2: { text: text.completed, class: 'bg-green-100 text-green-700' },
  3: { text: text.cancelled, class: 'bg-red-100 text-red-700' },
  4: { text: text.pendingApproval, class: 'bg-amber-100 text-amber-700' },
  6: { text: text.approvalRejected, class: 'bg-orange-100 text-orange-700' }
}

const emptyStateClass = computed(() => {
  if (props.compactEmpty) {
    return 'rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-500'
  }

  return 'py-16 text-center'
})

const paginationClass = computed(() => {
  if (props.compactEmpty) {
    return 'pagination-bridge'
  }

  return 'pagination-bridge'
})

const formatAmount = (amount) => Number(amount || 0).toLocaleString()
const getStatusClass = (status) => statusMap[status]?.class || 'bg-slate-100 text-slate-600'
const getStatusText = (status) => statusMap[status]?.text || text.unknown
const canEdit = (plan) => [0, 1, 4, 6].includes(plan.status) || plan.isDraft === 1
const showApprovalAction = (plan) => plan?.approvalInstanceId != null
</script>
